import { useMemo } from 'react';
import { addDays, isSameDay, parseISO, startOfMonth } from 'date-fns';
import { DayCell } from './DayCell';
import type { AvailabilityRule, DateSpecificAvailability, PTODay } from '../hooks/useAvailability';

const DAYS_OF_WEEK_HEADERS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

interface MonthGridProps {
  startDate: Date; // First day to show (should be start of a week, typically Sunday)
  weeksToShow: number; // Typically 5
  availability: AvailabilityRule[];
  dateOverrides: DateSpecificAvailability[];
  pto: PTODay[];
  onSelectDate: (date: Date, event: React.MouseEvent) => void;
  currentMonth: Date; // The "main" month being viewed (for graying out adjacent months)
}

export function MonthGrid({
  startDate,
  weeksToShow,
  availability,
  dateOverrides,
  pto,
  onSelectDate,
  currentMonth,
}: MonthGridProps) {
  const today = useMemo(() => new Date(), []);

  // Generate all days to display
  const days = useMemo(() => {
    const totalDays = weeksToShow * 7;
    return Array.from({ length: totalDays }, (_, i) => addDays(startDate, i));
  }, [startDate, weeksToShow]);

  // Helper to get recurring rule for a day of week
  const getRuleForDayOfWeek = (dayOfWeek: number): AvailabilityRule | null => {
    return availability.find((rule) => rule.dayOfWeek === dayOfWeek) ?? null;
  };

  // Helper to get date-specific override
  const getOverrideForDate = (date: Date): DateSpecificAvailability | null => {
    return dateOverrides.find((override) => {
      const overrideDate = parseISO(override.date);
      return isSameDay(overrideDate, date);
    }) ?? null;
  };

  // Helper to get PTO for a date
  const getPtoForDate = (date: Date): PTODay | null => {
    return pto.find((p) => isSameDay(parseISO(p.date), date)) ?? null;
  };

  // Split days into weeks for grid layout
  const weeks = useMemo(() => {
    const result: Date[][] = [];
    for (let i = 0; i < days.length; i += 7) {
      result.push(days.slice(i, i + 7));
    }
    return result;
  }, [days]);

  return (
    <div className="border border-border rounded-lg overflow-hidden bg-background">
      {/* Day of week headers */}
      <div className="grid grid-cols-7 bg-background-subtle border-b border-border">
        {DAYS_OF_WEEK_HEADERS.map((dayName) => (
          <div
            key={dayName}
            className="py-2 text-center text-xs font-medium text-foreground-muted border-r border-border last:border-r-0"
          >
            {dayName}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7">
        {weeks.map((week, weekIndex) => (
          week.map((date, dayIndex) => (
            <DayCell
              key={`${weekIndex}-${dayIndex}`}
              date={date}
              standardRule={getRuleForDayOfWeek(date.getDay())}
              dateOverride={getOverrideForDate(date)}
              pto={getPtoForDate(date)}
              isToday={isSameDay(date, today)}
              currentMonth={currentMonth}
              onSelect={onSelectDate}
            />
          ))
        ))}
      </div>
    </div>
  );
}
